import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useRouteMatch, useHistory } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { isEmpty } from 'lodash';
import { Button, Form, Modal, Skeleton, Switch } from 'antd';
import { useWindowSize } from '@/hooks';
import { useBreadcrumbs } from '@/contexts/BreadcrumbContextProvider';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { useCompaniesReduced } from '@/core/domain/enterprise/Companies/Companies.hooks';
import {
  useActiveOrInactivateTask,
  useDeleteTask,
  useTask,
} from '@/core/domain/operational/Tasks/Tasks.hooks';
import { PageProps } from '@/types/PageProps';
import TaskForm, {
  TaskFormData,
} from '@/components/Operational/Tasks/TaskForm';
import DefaultRouteTabs from '@/components/_common/DefaultRouteTabs';
import { SaveButton } from '@/components/_common/Buttons';
import { useTaskFormContext } from '../../TasksProvider';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import TaskFooter from '@/components/Operational/Tasks/TaskFooter';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';

function TaskGeneralInfoPage({ route }: PageProps) {
  const intl = useIntl();
  const [form] = Form.useForm<TaskFormData>();
  const history = useHistory();
  const { setParamLabel } = useBreadcrumbs();
  const { taskId } = useRouteMatch<{ taskId: string }>().params;
  const { isMobile } = useWindowSize();

  const { contextForm, active, setActive, isLoading, handleSubmit, disabled } =
    useTaskFormContext();

  const { data: task, isLoading: isLoadingTask } = useTask(+taskId);

  const { data: companyData } = useCompaniesReduced({
    active: true,
    rows: 50,
    isCompanyFilter: true,
  });

  const companies = flatInfiniteData(companyData);

  const deleteMutation = useDeleteTask();
  const activateOrInactivateMutation = useActiveOrInactivateTask();

  useEffect(() => {
    if (contextForm.generalTasks) {
      form.setFieldsValue(contextForm.generalTasks);
    } else if (task && !isEmpty(task) && companies.length) {
      form.setFieldsValue({
        ...task,
        subAreaId: +task?.subArea?.id,
        taskPriceSet: companies.map((company) => {
          const taskItem = task.taskPriceSet.find(
            (item) => item.companyId === company.id,
          );

          if (taskItem) {
            return {
              ...taskItem,
              price: taskItem.price,
              companyName: company.name,
            };
          }

          return {
            companyId: company.id,
            companyName: company.name,
          };
        }),
      });
    }
  }, [task, companies]);

  useEffect(() => {
    if (task) {
      setParamLabel(':taskId', task.description);
      setActive(task.active);
    }
  }, [task]);

  const handleRemoveClick = () => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'tasks.remove' }),
      content: `${intl.formatMessage({
        id: 'tasks.remove.alert',
      })} ${task?.description}`,
      onOk: async () => {
        // Fetch Service to remove area item.
        await deleteMutation.mutateAsync(+taskId);

        history.push('/app/operational/tasks');
      },
      okType: 'danger',
    });
  };

  const handleActiveClick = async (active: boolean) => {
    try {
      activateOrInactivateMutation.mutateAsync(Number(taskId));

      setActive(active);
    } catch {}
  };

  return (
    <>
      <Helmet
        title={intl.formatMessage({
          id: route.name,
        })}
      />

      <DefaultRouteTabs
        route={route.parent}
        permissions={route.permissions}
        baseKey={taskId}
        basePath={`/app/operational/tasks/${taskId}`}
        extra={
          <SpaceForButton flexDirection={isMobile ? 'column-reverse' : 'row'}>
            <Switch
              unCheckedChildren={intl.formatMessage({
                id: 'commons.entity.inactive',
              })}
              checkedChildren={intl.formatMessage({
                id: 'commons.entity.active',
              })}
              loading={isLoadingTask}
              checked={active}
              onChange={handleActiveClick}
              disabled={!hasPermissions([Authorities.REGISTER_TASK_EDIT])}
            />

            <Button
              danger
              block
              onClick={handleRemoveClick}
              loading={deleteMutation.isLoading}
              disabled={!hasPermissions([Authorities.REGISTER_TASK_DELETE])}
            >
              <FormattedMessage id="commons.form.button.remove" />
            </Button>

            <SaveButton
              block
              type="primary"
              onClick={async () => {
                await form.validateFields();
                handleSubmit();
              }}
              loading={isLoading}
              disabled={
                disabled || !hasPermissions([Authorities.REGISTER_TASK_EDIT])
              }
            >
              <FormattedMessage id="commons.form.button.save" />
            </SaveButton>
          </SpaceForButton>
        }
      >
        <Skeleton loading={isLoadingTask}>
          <TaskForm task={task} edit form={form} isLoading={isLoading} />

          <TaskFooter disabled={disabled} />
        </Skeleton>
      </DefaultRouteTabs>
    </>
  );
}

export default TaskGeneralInfoPage;
